import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clampStickVector, type StickVector } from "./control-types";
import { twMerge } from "tailwind-merge";
import { useTheme } from "@/hooks/useTheme";
type JoystickData = {
  x: number;
  y: number;
  distance: number;
  angle: { radian: number; degree: number };
  direction: { x: "left" | "right" | "center"; y: "up" | "down" | "center" };
  pressure: number;
};

type MarbleJoystickProps = {
  onMove: (data: JoystickData) => void;
  onStart?: (data: JoystickData) => void;
  onEnd?: (data: JoystickData) => void;
  className?: string;
  style?: React.CSSProperties;
  value?: StickVector;
};

const MAX_DISTANCE_RATIO = 0.2;
const JOYSTICK_SIZE_RATIO = 0.8;
export const MarbleJoystick: React.FC<MarbleJoystickProps> = ({
  onMove,
  onStart,
  onEnd,
  className = "",
  style = {},
  value,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const joystickRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const handleResize = () => {
      setSize(containerRef.current?.clientWidth ?? 0);
    };
    handleResize();

    // Use ResizeObserver to handle dynamic resizing
    const resizeObserver = new (window.ResizeObserver || (class {observe(){} disconnect(){}}))(
      handleResize
    );
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const [isActive, setIsActive] = useState(false);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [currentPosition, setCurrentPosition] = useState({ x: 0, y: 0 });
  const [maxDistance, setMaxDistance] = useState(size * MAX_DISTANCE_RATIO);
  const [isMoving, setIsMoving] = useState(false);


  // track active touch identifier
  const activeTouchRef = useRef<number | null>(null);

  // calculate joystick data from position
  const calculateJoystickData = useCallback(
    (x: number, y: number): JoystickData => {
      const distance = Math.min(Math.sqrt(x * x + y * y), maxDistance);
      const normalizedDistance = maxDistance > 0 ? distance / maxDistance : 0;

      const angle = {
        radian: Math.atan2(y, x),
        degree: (Math.atan2(y, x) * 180) / Math.PI,
      };

      // normalize coordinates to [-1, 1]
      const normalizedX =
        distance > 0 ? (x / distance) * normalizedDistance : 0;
      const normalizedY =
        distance > 0 ? (y / distance) * normalizedDistance : 0;

      return {
        x: normalizedX,
        y: normalizedY,
        distance: normalizedDistance,
        angle,
        direction: {
          x: (() => {
            if (x > 0.1) {
              return "right" as const;
            }
            if (x < -0.1) {
              return "left" as const;
            }
            return "center" as const;
          })(),
          y: (() => {
            if (y > 0.1) {
              return "up" as const;
            }
            if (y < -0.1) {
              return "down" as const;
            }
            return "center" as const;
          })(),
        },
        pressure: normalizedDistance,
      };
    },
    [maxDistance]
  );

  // handle touch/mouse events
  const handleStart = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      setCenter({ x: centerX, y: centerY });
      setIsActive(true);

      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > 0) {
        const clampedDistance = Math.min(distance, maxDistance);
        const clampedX = (deltaX / distance) * clampedDistance;
        const clampedY = (deltaY / distance) * clampedDistance;

        setCurrentPosition({ x: clampedX, y: clampedY });
        setIsMoving(true);

        const data = calculateJoystickData(clampedX, clampedY);
        onStart?.(data);
        onMove(data);
      } else {
        setCurrentPosition({ x: 0, y: 0 });
        setIsMoving(false);
        const data = calculateJoystickData(0, 0);
        onStart?.(data);
      }
    },
    [calculateJoystickData, onStart, onMove, maxDistance]
  );

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!(isActive && center.x && center.y)) {
        return;
      }

      const deltaX = clientX - center.x;
      const deltaY = clientY - center.y;

      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance === 0) {
        setCurrentPosition({ x: 0, y: 0 });
        setIsMoving(false);
        onMove(calculateJoystickData(0, 0));
        return;
      }

      const clampedDistance = Math.min(distance, maxDistance);
      const clampedX = (deltaX / distance) * clampedDistance;
      const clampedY = (deltaY / distance) * clampedDistance;

      setCurrentPosition((prev) => ({
        x: prev.x * 0.7 + clampedX * 0.3,
        y: prev.y * 0.7 + clampedY * 0.3,
      }));
      setIsMoving(true);

      const data = calculateJoystickData(clampedX, clampedY);
      onMove(data);
    },
    [isActive, center, maxDistance, calculateJoystickData, onMove]
  );

  const handleEnd = useCallback(() => {
    setIsActive(false);
    setIsMoving(false);
    setCurrentPosition({ x: 0, y: 0 });
    activeTouchRef.current = null;

    const data = calculateJoystickData(0, 0);
    onEnd?.(data);
  }, [calculateJoystickData, onEnd]);

  // touch event handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // prevent bubbling to parent controls
      e.stopPropagation();

      for (const touch of Array.from(e.changedTouches)) {
        if (touch) {
          // check if touch is within joystick bounds
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const x = touch.clientX - rect.left;
            const y = touch.clientY - rect.top;

            if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
              // this touch started on our joystick
              activeTouchRef.current = touch.identifier;
              handleStart(touch.clientX, touch.clientY);
              break;
            }
          }
        }
      }
    },
    [handleStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();

      if (activeTouchRef.current === null) {
        return;
      }

      for (const touch of Array.from(e.touches)) {
        if (touch && touch.identifier === activeTouchRef.current) {
          handleMove(touch.clientX, touch.clientY);
          break;
        }
      }
    },
    [handleMove]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();

      if (activeTouchRef.current === null) {
        return;
      }

      for (const touch of Array.from(e.changedTouches)) {
        if (touch && touch.identifier === activeTouchRef.current) {
          handleEnd();
          break;
        }
      }
    },
    [handleEnd]
  );

  // mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleStart(e.clientX, e.clientY);
    },
    [handleStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleMove(e.clientX, e.clientY);
    },
    [handleMove]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleEnd();
    },
    [handleEnd]
  );

  // global mouse up handler (use capture so it fires even if another element stops propagation)
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isActive) {
        handleEnd();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp, { capture: true });
    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp, { capture: true } as EventListenerOptions);
    };
  }, [isActive, handleEnd]);

  // global mouse move handler while active drag (keeps updating outside bounds)
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const handleGlobalMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [isActive, handleMove]);

  useEffect(() => {
    setMaxDistance(size * MAX_DISTANCE_RATIO);
  }, [size]);

  const externalPosition = useMemo(() => {
    if (!value) {
      return null;
    }
    const { x, y } = clampStickVector(value);
    return {
      x: x * maxDistance,
      y: y * maxDistance,
    };
  }, [value, maxDistance]);

  useEffect(() => {
    if (!externalPosition || isActive) {
      return;
    }
    setCurrentPosition(externalPosition);
    const isMovingNow =
      Math.abs(externalPosition.x) > 0.001 || Math.abs(externalPosition.y) > 0.001;
    setIsMoving(isMovingNow);
  }, [externalPosition, isActive]);

  const { theme } = useTheme();

  return (
    <div
      aria-label="Joystick"
      aria-valuemax={1}
      aria-valuemin={-1}
      aria-valuenow={Math.round(currentPosition.y * 100) / 100}
      className={twMerge("cursor-move", className)}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      ref={containerRef}
      role="slider"
      style={{
        position: "relative",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        ...style,
      }}
      tabIndex={0}
    >
      {/* Background circle */}
      <div
        className={`w-full h-full backdrop-blur-2xl ${theme === "dark" ? "bg-white/5" : "bg-black/5"} rounded-full`}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      {/* Joystick handle */}
      <div
        ref={joystickRef}
        className="bg-fg-primary"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: size * JOYSTICK_SIZE_RATIO,
          height: size * JOYSTICK_SIZE_RATIO,
          borderRadius: "50%",
          transform: `translate(-50%, -50%) translate(${currentPosition.x}px, ${currentPosition.y}px)`,
          pointerEvents: "none",
          transition: isActive ? "none" : "transform 0.1s ease-out",
          zIndex: 2,
        }}
      />
    </div>
  );
};
